import { Form, type FormInstance } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CustomSelect } from "sipli-fleet-core/components";
import {
  useQueryGetWarehouseChildLookup,
  useQueryGetWarehouseLookup,
} from "src/queries/warehouses.queries";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import {
  getLookupOptions,
  getOptionalDistributorId,
  mapLookupOptions,
  type UnitFormValues,
} from "./unitFormShared";

export interface PositionFieldsProps {
  form: FormInstance<UnitFormValues>;
  open: boolean;
}

const PositionFields = ({ form, open }: PositionFieldsProps) => {
  const { t } = useTranslation();
  const { selectedDistributor } = useSelectDistributor();
  const distributorId = getOptionalDistributorId(selectedDistributor);

  const selectedWarehouseId = Form.useWatch("warehouseId", form);
  const selectedAreaId = Form.useWatch("areaId", form);
  const selectedLaneId = Form.useWatch("laneId", form);
  const selectedShelfId = Form.useWatch("shelfId", form);

  const warehousesQuery = useQueryGetWarehouseLookup(
    distributorId ? { idDistributor: distributorId } : {},
    open
  );
  const areasQuery = useQueryGetWarehouseChildLookup("area", selectedWarehouseId, open);
  const lanesQuery = useQueryGetWarehouseChildLookup("lane", selectedAreaId, open);
  const shelvesQuery = useQueryGetWarehouseChildLookup("shelf", selectedLaneId, open);
  const positionsQuery = useQueryGetWarehouseChildLookup("position", selectedShelfId, open);

  const warehouseOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(warehousesQuery.data?.data)),
    [warehousesQuery.data?.data]
  );
  const areaOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(areasQuery.data?.data)),
    [areasQuery.data?.data]
  );
  const laneOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(lanesQuery.data?.data)),
    [lanesQuery.data?.data]
  );
  const shelfOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(shelvesQuery.data?.data)),
    [shelvesQuery.data?.data]
  );
  const positionOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(positionsQuery.data?.data)),
    [positionsQuery.data?.data]
  );

  const handleWarehouseChange = () => {
    form.setFieldsValue({
      areaId: undefined,
      laneId: undefined,
      shelfId: undefined,
      positionId: undefined,
    });
  };

  const handleAreaChange = () => {
    form.setFieldsValue({ laneId: undefined, shelfId: undefined, positionId: undefined });
  };

  const handleLaneChange = () => {
    form.setFieldsValue({ shelfId: undefined, positionId: undefined });
  };

  const handleShelfChange = () => {
    form.setFieldValue("positionId", undefined);
  };

  return (
    <div className="grid gap-x-3 md:grid-cols-2">
      <Form.Item
        name="warehouseId"
        label={`${t("warehouse")}*`}
        rules={[{ required: true, message: t("field_required") }]}
      >
        <CustomSelect
          placeholder={t("select_warehouse")}
          options={warehouseOptions}
          loading={warehousesQuery.isLoading}
          onChange={handleWarehouseChange}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item
        name="areaId"
        label={`${t("area")}*`}
        rules={[{ required: true, message: t("field_required") }]}
      >
        <CustomSelect
          placeholder={t("select_area")}
          options={areaOptions}
          loading={areasQuery.isLoading}
          disabled={!selectedWarehouseId}
          onChange={handleAreaChange}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item
        name="laneId"
        label={`${t("lane")}*`}
        rules={[{ required: true, message: t("field_required") }]}
      >
        <CustomSelect
          placeholder={t("select_lane")}
          options={laneOptions}
          loading={lanesQuery.isLoading}
          disabled={!selectedAreaId}
          onChange={handleLaneChange}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item
        name="shelfId"
        label={`${t("shelf")}*`}
        rules={[{ required: true, message: t("field_required") }]}
      >
        <CustomSelect
          placeholder={t("select_shelf")}
          options={shelfOptions}
          loading={shelvesQuery.isLoading}
          disabled={!selectedLaneId}
          onChange={handleShelfChange}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item
        name="positionId"
        label={`${t("position")}*`}
        rules={[{ required: true, message: t("field_required") }]}
      >
        <CustomSelect
          placeholder={t("select_position")}
          options={positionOptions}
          loading={positionsQuery.isLoading}
          disabled={!selectedShelfId}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
    </div>
  );
};

export default PositionFields;
